/**
 * The follow step (design D1, D6): given the trail and the player, where does the
 * follower end up one frame from now. Steering, not teleporting — the follower turns its
 * yaw toward the bearing of its target, then advances along its own facing, so a follower
 * caught facing away from its target pivots on the spot before it sets off, which is what
 * makes a corner read as a turn rather than a slide.
 *
 * `yaw` follows the same convention as `Spawn.yaw` in `profile/types.ts`: radians,
 * counter-clockwise from -Z, so a yaw of 0 faces `(0, -1)` in the XZ plane.
 *
 * Pure and allocation-free on the hot path: `stepFollow` mutates and returns the pose
 * object the caller owns, rather than allocating a new one every frame.
 */

import { RESUME_RADIUS, STOP_RADIUS, trailBacklog, type Trail } from './trail';
import type { Vec3 } from './profile/types';

/** A shade over the player's walk, so the dog closes the gap rather than holding it. */
export const DOG_WALK_SPEED = 3.4;

/** Over the player's sprint, so the dog can never fall permanently behind. */
export const DOG_RUN_SPEED = 6.2;

/** Backlog, in metres of corridor, over which speed ramps DOG_WALK_SPEED -> DOG_RUN_SPEED. */
export const CATCHUP_DISTANCE = 6;

/** The player's own CLIMB_SPEED (`climbing.ts`) — a dog rises a ladder no faster than its owner. */
export const DOG_CLIMB_SPEED = 2.0;

/** A 90-degree turn in about a quarter of a second. */
export const MAX_TURN_RATE = 6;

/**
 * How far the nose tips up at DOG_CLIMB_SPEED. Not pinned by any physical fact — it only
 * has to read as "going up the ladder" rather than "floating up beside it" (design D6,
 * risk 1), and 30 degrees is a clearly visible tilt without looking like a stumble.
 */
export const MAX_CLIMB_PITCH = Math.PI / 6;

/** The follower's pose: what the renderer reads, and what `stepFollow` mutates in place. */
export type FollowerPose = {
  position: Vec3;
  /** Radians, counter-clockwise from -Z — see the file comment. */
  yaw: number;
  /** Radians, positive nose-up. Tracks vertical rate, capped at MAX_CLIMB_PITCH. */
  pitch: number;
  /** Horizontal distance covered this step — what the gait integrates into its phase. */
  distanceMoved: number;
  /** Hysteresis state for the STOP_RADIUS / RESUME_RADIUS pair; not read by the renderer. */
  resting: boolean;
};

/** A fresh pose at `position`, facing `yaw`, at rest. */
export function createFollowerPose(position: Vec3, yaw: number): FollowerPose {
  return {
    position: { x: position.x, y: position.y, z: position.z },
    yaw,
    pitch: 0,
    distanceMoved: 0,
    resting: false,
  };
}

function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

/** Wraps an angle into (-PI, PI], so a heading error is always the short way round. */
function normalizeAngle(angle: number): number {
  let wrapped = angle % (2 * Math.PI);
  if (wrapped > Math.PI) wrapped -= 2 * Math.PI;
  if (wrapped < -Math.PI) wrapped += 2 * Math.PI;
  return wrapped;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** The queue's front point, or the player themselves once the queue has run dry (D1). */
function targetOf(trail: Trail, player: Vec3): Vec3 {
  return trail.points[0] ?? player;
}

/**
 * Advances `pose` by one step of `delta` seconds, given the trail's queue and the
 * player's own position. Mutates and returns `pose` — the caller owns it, and this never
 * allocates.
 */
export function stepFollow(pose: FollowerPose, trail: Trail, player: Vec3, delta: number): FollowerPose {
  pose.distanceMoved = 0;
  if (delta <= 0) return pose;

  const target = targetOf(trail, player);
  const distanceToTarget = distance(pose.position, target);

  if (pose.resting) {
    if (distanceToTarget < RESUME_RADIUS) return pose;
    pose.resting = false;
  } else if (distanceToTarget <= STOP_RADIUS) {
    pose.resting = true;
    return pose;
  }

  // Steer: turn toward the bearing, then advance along the (possibly still-turning) facing,
  // scaled by how square-on it already was — see the file comment.
  const dx = target.x - pose.position.x;
  const dz = target.z - pose.position.z;
  const groundDistance = Math.hypot(dx, dz);
  const bearing = groundDistance > 1e-6 ? Math.atan2(-dx, -dz) : pose.yaw;
  const headingError = normalizeAngle(bearing - pose.yaw);

  const maxTurn = MAX_TURN_RATE * delta;
  const turn = clamp(headingError, -maxTurn, maxTurn);
  pose.yaw = normalizeAngle(pose.yaw + turn);

  const backlog = trailBacklog(trail, pose.position, player);
  const catchUp = clamp(backlog / CATCHUP_DISTANCE, 0, 1);
  const speed = DOG_WALK_SPEED + catchUp * (DOG_RUN_SPEED - DOG_WALK_SPEED);
  const forwardSpeed = speed * Math.max(0, Math.cos(headingError));
  const moveDistance = forwardSpeed * delta;

  const forwardX = -Math.sin(pose.yaw);
  const forwardZ = -Math.cos(pose.yaw);
  pose.position.x += forwardX * moveDistance;
  pose.position.z += forwardZ * moveDistance;
  pose.distanceMoved = moveDistance;

  const verticalGap = target.y - pose.position.y;
  const verticalStep = clamp(verticalGap, -DOG_CLIMB_SPEED * delta, DOG_CLIMB_SPEED * delta);
  pose.position.y += verticalStep;
  const verticalRate = verticalStep / delta;
  pose.pitch = clamp((verticalRate / DOG_CLIMB_SPEED) * MAX_CLIMB_PITCH, -MAX_CLIMB_PITCH, MAX_CLIMB_PITCH);

  return pose;
}
